<template>
	<KtField v-bind="{ field }" :helpTextSlot="$slots.helpText" useFieldset>
		<template #container>
			<div :class="rootClasses" :forceUpdateKey="forceUpdateKey">
				<div
					v-for="(option, index) in options"
					:key="index"
					class="kt-field-radio-group__container"
				>
					<div class="kt-field-radio-group__header">
						<label
							class="kt-field-radio-group__label"
							:class="{
								'kt-field-radio-group__label--disabled':
									field.isDisabled || Boolean(option.isDisabled),
							}"
							:data-test="optionDataTest(option)"
						>
							<input
								v-bind="inputProps(option, index)"
								@change="onChange(option.value)"
							/>
							<ToggleRadio
								class="kt-field-radio-group__radio"
								:isChecked="field.currentValue === option.value"
								:isDisabled="field.isDisabled || Boolean(option.isDisabled)"
							/>
							<slot name="header" :option="option">
								<div v-text="option.label" />
							</slot>
						</label>
						<FieldHelpText
							v-if="option.tooltip"
							class="kt-field-radio-group__tooltip"
							:helpText="option.tooltip"
						/>
						<slot name="headerSide" :option="option" />
					</div>
					<div class="kt-field-radio-group__content">
						<slot name="content" :option="option" />
					</div>
				</div>
			</div>
		</template>
	</KtField>
</template>

<script lang="ts">
import omit from 'lodash/omit.js'
import { computed, defineComponent, type InputHTMLAttributes } from 'vue'

import { KtField } from '../kotti-field'
import FieldHelpText from '../kotti-field/components/FieldHelpText.vue'
import { useField, useForceUpdate } from '../kotti-field/hooks'
import { makeProps } from '../make-props'
import ToggleRadio from '../shared-components/ToggleRadio.vue'

import { KOTTI_FIELD_RADIO_GROUP_SUPPORTS } from './constants'
import { KottiFieldRadioGroup } from './types'

export default defineComponent({
	name: 'KtFieldRadioGroup',
	components: {
		FieldHelpText,
		KtField,
		ToggleRadio,
	},
	props: makeProps(KottiFieldRadioGroup.propsSchema),
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const field = useField<KottiFieldRadioGroup.ModelValue>({
			emit,
			isEmpty: (value) => value === null,
			props,
			supports: KOTTI_FIELD_RADIO_GROUP_SUPPORTS,
		})

		const { forceUpdate, forceUpdateKey } = useForceUpdate()

		return {
			field,
			forceUpdateKey: forceUpdateKey.value,
			inputProps: computed(
				() =>
					(
						option: KottiFieldRadioGroup.PropsInternal['options'][0],
						index: number,
					): InputHTMLAttributes & { class: string } => ({
						...omit(field.inputProps, ['data-test', 'id']),
						checked: field.currentValue === option.value,
						class: 'kt-field-radio-group__input',
						disabled: field.isDisabled || Boolean(option.isDisabled),
						id: `${field.inputProps.id}-${String(index)}`,
						name: field.inputProps.id,
						type: 'radio',
						value: option.value,
					}),
			),
			onChange: (value: KottiFieldRadioGroup.Entry['value']) => {
				field.setValue(value)

				forceUpdate()
			},
			optionDataTest: (option: KottiFieldRadioGroup.Entry) => {
				if (option.dataTest) return option.dataTest

				if (Object.keys(field.inputProps).includes('data-test')) {
					return [field.inputProps['data-test'], option.value].join('.')
				}
			},
			rootClasses: computed(() => ({
				'kt-field-radio-group': true,
				'kt-field-radio-group--inline': props.isInline,
			})),
		}
	},
})
</script>

<style lang="scss">
@import '../kotti-field/mixins';

.kt-field-radio-group {
	display: flex;
	flex: 1;

	&--inline {
		flex-direction: row;

		.kt-field-radio-group__container:not(:first-child) {
			margin-left: 1rem;
		}
	}

	&:not(&--inline) {
		flex-direction: column;

		.kt-field-radio-group__container:not(:first-child) {
			margin-top: 0.4rem;
		}
	}

	&__header {
		display: flex;
		align-items: flex-start;

		> *:not(:last-child) {
			margin-right: 0.3rem;
		}
	}

	&__label {
		display: flex;
		align-items: flex-start;
		cursor: pointer;

		> *:not(:last-child) {
			margin-right: 0.3rem;
		}

		&--disabled {
			color: var(--text-05);
			cursor: not-allowed;

			.kt-field-radio-group__radio {
				border-color: var(--ui-02);

				&--checked {
					background-color: var(--ui-02);
					box-shadow: var(--shadow-base);
				}
			}
		}
	}

	&__tooltip {
		// align tooltip icon with the center of the first line of the label
		// (assumption: font-size comes from common parent element)
		//  > starting point is upper end of the container (flex-start)
		//  > (+0.75em) Put upper edge of element into center (since line-height = 1.5 * font-size)
		//  > (-6px) Put it up half the height of the tooltip height (12px)
		transform: translateY(calc(0.75em - 6px));
	}

	&__input {
		display: none;
	}

	&__radio {
		// align radio with the center of the first line of the label
		// (assumption: font-size comes from common parent element)
		//  > starting point is upper end of the container (flex-start)
		//  > (+0.75em) Put upper edge of element into center (since line-height = 1.5 * font-size)
		//  > (-var(--radio-size) * 0.5) Put it up half the height of the radio height
		transform: translateY(calc(0.75em - var(--radio-size) * 0.5));
	}

	&__radio-inside {
		display: block;
		width: var(--radio-inside-side);
		height: var(--radio-inside-side);
		background-color: var(--ui-background);
		border-radius: 50%;
	}

	&__content {
		font-size: var(--font-size-small);
	}
}

.kt-field__wrapper {
	@include validations using ($type) {
		@if $type != empty {
			&:not(.kt-field__wrapper--disabled) {
				.kt-field-radio-group {
					&__radio {
						border-color: var(--support-#{$type});

						&--checked {
							background-color: var(--support-#{$type});
							box-shadow: var(--shadow-base);
						}
					}
				}
			}
		}
	}
}
</style>
